import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import InstrumentRowContainer from '@alphapoint/apex-web/lib/components/InstrumentRow';
import PriceChart from '@alphapoint/apex-web/lib/components/PriceChart/PriceChartContainer';
import DepthChart from '@alphapoint/apex-web/lib/components/DepthChart/DepthChartContainer';
import OrderHistoryComponent from '@alphapoint/apex-web/lib/components/OrderHistoryComponents';
import MarginOrderHistoryComponent from '@alphapoint/apex-web/lib/components/OrderHistoryComponents/MarginOrderHistory';
import OrderBook from '@alphapoint/apex-web/lib/components/OrderBookComponents/OrderBookContainer';
import RecentTrades from '@alphapoint/apex-web/lib/components/RecentTrades/RecentTradesContainer';
import OrderEntry from '@alphapoint/apex-web/lib/components/OrderEntryComponents/defaultForm/OrderEntryContainer';
import InstrumentPositionsWrapperContainer from '@alphapoint/apex-web/lib/components/InstrumentPositionsWrapper';
import { getBEMClasses } from '@alphapoint/apex-web/lib/helpers/cssClassesHelper';
import { isMarginActiveSelector } from '@alphapoint/apex-web/lib/redux/margin/selectors';
import { initSelectedInstrument } from '@alphapoint/apex-web/lib/redux/actions/instrumentActions';

import config from '../../config';
import './TradingPage.css';

const cls = getBEMClasses('exchange');

const Tabs = ({ items, className, name }) => {
  const [activeTab, setActiveTab] = useState(items[0].id);

  return (
    <div className={`${className} ${cls('tabs')}`}>
      {items.map((item) => (
        <React.Fragment key={item.id}>
          <label>
            {item.title}
            <input
              type="radio"
              name={name}
              aria-controls={item.id}
              checked={activeTab === item.id}
              onClick={() => setActiveTab(item.id)}
            />
          </label>
        </React.Fragment>
      ))}
    </div>
  );
};

Tabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** id of the element a tab controls */
      id: PropTypes.string.isRequired,
      /** title of the tab */
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string.isRequired,
  /** name of the tabs group */
  name: PropTypes.string.isRequired,
};

const {
  OrderHistoryData: { usePagination },
  TradingLayout: { maxLines },
} = config;

const TradingLayout = ({ isMarginActive, selectedInstrumentId, instruments, initSelectedInstrument }) => {
  const { symbol } = useParams();
  const history = useHistory();

  const lastInitializedSymbolRef = useRef(null);
  const initializedByURLRef = useRef(false);
  const hasSyncedRef = useRef(false);

  const getSymbolKey = (instrument) => `${instrument.Product1Symbol}${instrument.Product2Symbol}`.toUpperCase();

  useEffect(() => {
    const upperSymbol = symbol?.toUpperCase();
  const selected = instruments.find((i) => i.InstrumentId === selectedInstrumentId);
  const selectedSymbol = selected ? getSymbolKey(selected) : null;
  const symbolExists = instruments.some((i) => getSymbolKey(i) === upperSymbol);

  // if the symbol does not exist
  if (upperSymbol && !symbolExists && selectedSymbol) {
    history.replace(`/exchange/${selectedSymbol}`);
    return;
  }

    if (upperSymbol && lastInitializedSymbolRef.current !== upperSymbol) {
      initializedByURLRef.current = true;
      hasSyncedRef.current = false;
      lastInitializedSymbolRef.current = upperSymbol;
      initSelectedInstrument(upperSymbol);
      return;
    }

    if (initializedByURLRef.current && selectedSymbol === upperSymbol) {
      hasSyncedRef.current = true;
      initializedByURLRef.current = false;
      return;
    }

    if (!upperSymbol || selectedSymbol) {
      lastInitializedSymbolRef.current = selectedSymbol;
      history.push(`/exchange/${selectedSymbol}`);
      return;
    }

    if (!hasSyncedRef.current) return;

    if (selectedSymbol && selectedSymbol !== upperSymbol) {
      lastInitializedSymbolRef.current = selectedSymbol;
      hasSyncedRef.current = false;
      history.push(`/exchange/${selectedSymbol}`);
    }
  }, [symbol, selectedInstrumentId, instruments, initSelectedInstrument, history]);

  const orderHistoryConfig = {
    usePagination,
    maxLines,
    filterMode: 'selectedInstrument',
  };

  return (
    <div className={cls('container')}>
      <div className={cls('instrument-row')}>
        <InstrumentRowContainer />
      </div>
      <div className={cls('content')}>
        <div className={cls('charts-area')}>
          <Tabs
            items={[
              { id: 'price-chart', title: 'Price Chart' },
              { id: 'depth-chart', title: 'Depth Chart' },
            ]}
            name="charts"
            className={cls('charts-tabs')}
          />
          <div id="price-chart">
            <PriceChart />
          </div>
          <div id="depth-chart">
            <DepthChart smallScreen />
          </div>
        </div>

        <div className={cls('order-book-area')}>
          <Tabs
            items={[
              { id: 'order-book', title: 'Order Book' },
              { id: 'recent-trades', title: 'Recent Trades' },
            ]}
            name="order-book"
            className={cls('order-book-tabs')}
          />
          <div id="order-book">
            <div className="trade-component__header">Order Book</div>
            <OrderBook />
          </div>
          <div id="recent-trades">
            <div className="trade-component__header">Recent Trades</div>
            <RecentTrades />
          </div>
        </div>

        <div className={cls('order-entry-area')}>
          <Tabs
            items={[
              { id: 'order-entry', title: 'Order Entry' },
              { id: 'balances', title: 'Balances' },
            ]}
            name="order-entry"
            className={cls('order-entry-tabs')}
          />
          <div id="order-entry">
            <div className="trade-component__header">Order Entry</div>
            <OrderEntry />
          </div>
          <div id="balances">
            <InstrumentPositionsWrapperContainer />
          </div>
        </div>

        <div className={cls('orders-area')}>
          {isMarginActive ? (
            <MarginOrderHistoryComponent config={orderHistoryConfig} />
          ) : (
            <OrderHistoryComponent config={orderHistoryConfig} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isMarginActive: isMarginActiveSelector(state),
  selectedInstrumentId: state.instrument.selectedInstrumentId,
  instruments: state.instrument.instruments,
});

const mapDispatchToProps = {
  initSelectedInstrument,
};

export const TradingPage = connect(mapStateToProps, mapDispatchToProps)(TradingLayout);
